import React, { useState, useEffect, useCallback } from "react";
import EnrollmentCard from "./enrollmentCard/EnrollmentCard";
import EnrollmentEmployeeCard from "./enrollmentEmployeeCard/EnrollmentEmployeeCard";
import useRouteParam from "../../../../../../hooks/useRouteParam";
import { api } from "../../../../../../utils/api";
import { getActivePlans, getActiveVEBPlans } from "../../../../../../utils/api/plan/plan";
// import {
// 	PFB_CORE_ENROLLMENT_SHORT_NAMES,
// 	BBI_VEB_ENROLLMENT_SHORT_NAMES
// } from "../../../../../../constants/BBIEnrollmentPlansShortName";
import {
	ActiveEmployeeCount,
	SpecificEnrollmentCardGridProps
} from "../../../../../../@types/components/enrollmentCardGrid.types";
import { LegendIndicator } from "../../../../../../@types/charts/barCharts.types";

import "./enrollmentCardGrid.css";
import NoEnrollmentFoundCard from "./enrollmentCard/noEnrollmentFoundCard/NoEnrollmentFoundCard";
import { PFB_CORE_ENROLLMENT_SHORT_NAMES } from "../../../../../../constants/PFBEnrollmentPlansShortName";
import { BBI_VEB_ENROLLMENT_SHORT_NAMES } from "../../../../../../constants/BBIEnrollmentPlansShortName";

const EnrollmentCardGridPFB: React.FC<SpecificEnrollmentCardGridProps> = ({ refresh, setRefresh }) => {
	const [isVEB, setIsVEB] = useState(false);
	const [hasCorePlans, setHasCorePlans] = useState<boolean | null>(null);
	const [highlightsTotalCount, setHighlightsTotalCount] = useState<{
		totalCoreEnrollments: number | null;
		totalEmployeeCount: number;
		totalVebEnrollments: number;
	}>({
		totalCoreEnrollments: 0,
		totalEmployeeCount: 0,
		totalVebEnrollments: 0
	});
	const [activeEmployeeCount, setActiveEmployeeCount] = useState<ActiveEmployeeCount>({
		male: 0,
		female: 0
	});
	const [coreEnrollmentsGraphData, setCoreEnrollmentsGraphData] = useState({
		labels: ["HP", "DT", "VI"],
		datasets: [
			{
				// Zero until the real fetch resolves — never a plausible-looking placeholder number.
				data: [0, 0, 0],
				backgroundColor: [
					"#F08080",
					"#F28C87",
					"#F59D92",
					"#F6A296",
					"#F8AD9D",
					"#FAB9A4",
					"#FBC4AB",
					"#FDCFB2",
					"#FFDAB9"
				],
				borderColor: [
					"#F08080",
					"#F28C87",
					"#F59D92",
					"#F6A296",
					"#F8AD9D",
					"#FAB9A4",
					"#FBC4AB",
					"#FDCFB2",
					"#FFDAB9"
				],
				borderWidth: 1
			}
		]
	});
	const [VEBEnrollmentsGraphData, setVEBEnrollmentsGraphData] = useState({
		labels: [
			"Cancer",
			"Whole Life",
			"STD",
			"Critical Illness",
			"HI",
			"Accident",
			"LII",
			"TLA",
			"Doc & Rx",
			"Vision",
			"Dental"
		],
		datasets: [
			{
				// Zero until the real fetch resolves — never a plausible-looking placeholder number.
				data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
				backgroundColor: [
					"#615ae7",
					"#716be1",
					"#7772df",
					"#8681df",
					"#9490e0",
					"#9995df",
					"#a39fdf",
					"#b0ade1",
					"#bebce1", //
					"#cccbe7 ",
					"#d8d7ea"
				],
				borderColor: [
					"#615ae7",
					"#716be1",
					"#7772df",
					"#8681df",
					"#9490e0",
					"#9995df",
					"#a39fdf",
					"#b0ade1",
					"#bebce1",
					"#cccbe7 ",
					"#d8d7ea"
				],
				borderWidth: 1
			}
		]
	});
	const [totalEmployeeGraphData, setTotalEmployeeGraphData] = useState({
		labels: ["Female", "Male"],
		datasets: [
			{
				// Zero until the real fetch resolves — never a plausible-looking placeholder number.
				data: [0, 0],
				backgroundColor: ["#4059ad", "#6b9ac4"],
				hoverBackgroundColor: ["#617acc", "#81b3df"]
			}
		]
	});
	const [coreEnrollmentsLegendData, setCoreEnrollmentsLegendData] = useState<LegendIndicator[]>([
		{ labelName: "HP", colorIndicator: "#F08080" },
		{ labelName: "DT", colorIndicator: "#F28C87" },
		{ labelName: "VI", colorIndicator: "#F59D92" }
		// { labelName: "GLTD 1", colorIndicator: "#F6A296" },
		// { labelName: "GLTD 2", colorIndicator: "#F8AD9D" },
		// { labelName: "HOO", colorIndicator: "#FAB9A4" },
		// { labelName: "KSOP", colorIndicator: "#FBC4AB" },
		// { labelName: "GLI", colorIndicator: "#FDCFB2" },
		// { labelName: "CCE", colorIndicator: "#FFDAB9" }
	]);
	const [VEBEnrollmentsLegendData, setVEBEnrollmentsLegendData] = useState<LegendIndicator[]>([
		{ labelName: "Cancer", colorIndicator: "#615ae7" },
		{ labelName: "Whole Life", colorIndicator: "#716be1" },
		{ labelName: "STD", colorIndicator: "#7772df" },
		{ labelName: "Critical Illness", colorIndicator: "#8681df" },
		{ labelName: "HI", colorIndicator: "#9490e0" },
		{ labelName: "Accident", colorIndicator: "#9995df" },
		{ labelName: "GLI", colorIndicator: "#a39fdf" },
		{ labelName: "FPP", colorIndicator: "#b0ade1" },
		{ labelName: "Doc & Rx", colorIndicator: "#bebce1" },
		{ labelName: "Vision", colorIndicator: "#cccbe7" },
		{ labelName: "Dental", colorIndicator: "#d8d7ea" }
	]);

	const queryGroupNumber = useRouteParam("gn");

	const coreEnrollmentOptions = {
		scales: {
			x: {
				type: "category",
				labels: ["HP", "DT", "VI"]
			},
			y: {
				beginAtZero: true
			}
		},
		plugins: {
			legend: {
				display: false,
				position: "bottom",
				labels: {
					color: "#000" // Set the color you want for the legend labels
				},
				generateLabels: (datasets: any) =>
					datasets.map((dataset: any) => ({
						text: dataset.label,
						fillStyle: dataset.backgroundColor,
						hidden: dataset.hidden,
						lineCap: "round",
						lineDash: [],
						lineDashOffset: 0,
						lineJoin: "round",
						lineWidth: 1,
						strokeStyle: dataset.borderColor,
						pointStyle: "circle",
						datasetIndex: dataset.index
					}))
			}
		}
	};
	// =======================================VEB====================================

	const vebEnrollmentOptions = {
		scales: {
			x: {
				type: "category",
				labels: [
					"Cancer",
					"Whole Life",
					"STD",
					"Critical Illness",
					"HI",
					"Accident",
					"GLI",
					"FPP",
					"Doc & Rx",
					"Vision",
					"Dental"
				],
				ticks: {
					font: {
						size: 12 // Adjust the font size for x-axis labels
					}
				}
			},
			y: {
				beginAtZero: true,
				ticks: {
					font: {
						size: 12 // Adjust the font size for x-axis labels
					}
				}
			}
		},
		plugins: {
			legend: {
				display: false,
				position: "bottom",
				labels: {
					color: "#000" // Set the color you want for the legend labels
				},
				generateLabels: (datasets: any) =>
					datasets.map((dataset: any) => ({
						text: dataset.label,
						fillStyle: dataset.backgroundColor,
						hidden: dataset.hidden,
						lineCap: "round",
						lineDash: [],
						lineDashOffset: 0,
						lineJoin: "round",
						lineWidth: 1,
						strokeStyle: dataset.borderColor,
						pointStyle: "circle",
						datasetIndex: dataset.index
					}))
			}
		}
	};

	const totalEmployeeoptions = {
		plugins: {
			legend: {
				display: true,
				position: "bottom"
			}
		}
	};
	const getInitBenefitSummaryHighlightDetails = useCallback(async (queryGroupNumber: number) => {
		// The enrollment-highlights totals can't tell "no plans configured" apart from "plans exist but
		// nobody's enrolled" — both come back as a count of 0. Ask the plan collection directly.
		const [planHighlightDetails, corePlansResponse, vebPlansResponse] = await Promise.all([
			api.benefitSummary.getBenefitSummaryPlanHighlights(queryGroupNumber),
			getActivePlans(queryGroupNumber).catch(() => null),
			getActiveVEBPlans(queryGroupNumber).catch(() => null)
		]);
		// console.log("RESPONSE------->", planHighlightDetails);

		let _totalActiveVEBEnrollmentCount = 0;
		let _VEBEnrollmentBarChartData: any[] = [];

		const isVEB = (vebPlansResponse?.currentPlans?.length ?? 0) > 0;
		const _hasCorePlans = (corePlansResponse?.length ?? 0) > 0;
		setIsVEB(isVEB);
		setHasCorePlans(_hasCorePlans);

		if (planHighlightDetails) {
			const { activeCoreEnrollmentCountsWithIndividualPlan, activeEmployeeCountWithGender } =
				planHighlightDetails;

			// console.log("ResponseResponse", planHighlightDetails);

			// Extract total counts
			const _totalActiveCoreEnrollmentCount = _hasCorePlans
				? activeCoreEnrollmentCountsWithIndividualPlan.totalActiveCoreEnrollmentCount
				: null;
			const _totalActiveEmployeeCount = activeEmployeeCountWithGender.totalActiveEmployeeCount;

			// Check if VEB is enabled and destructure data accordingly
			if (isVEB && planHighlightDetails.activeVEBEnrollmentCountsWithIndividualPlan) {
				const { totalActiveVEBEnrollmentCount, plans } =
					planHighlightDetails.activeVEBEnrollmentCountsWithIndividualPlan;
				_totalActiveVEBEnrollmentCount = totalActiveVEBEnrollmentCount;
				_VEBEnrollmentBarChartData = plans;
			}

			// Male and Female count
			let _totalmaleEmployeeCount = 0,
				_totalfemaleEmployeeCount = 0;
			activeEmployeeCountWithGender.genders.forEach((elm: any) => {
				if (elm.gender === "MALE") _totalmaleEmployeeCount += elm.count;
				else if (elm.gender === "FEMALE") _totalfemaleEmployeeCount += elm.count;
			});

			// Process core enrollments
			const _coreEnrollmentBarChartData: any[] = activeCoreEnrollmentCountsWithIndividualPlan.plans;
			const _storedCoreEnrollmentArrayData: number[] = [];
			//eslint-disable-next-line
			Object.values(PFB_CORE_ENROLLMENT_SHORT_NAMES).forEach((renderedPlan) => {
				_coreEnrollmentBarChartData.forEach(({ plan, count }) => {
					if (renderedPlan === plan) _storedCoreEnrollmentArrayData.push(count);
				});
			});

			// Process VEB enrollments (if isVEB is true)
			const _storedVEBEnrollmentArrayData: number[] = [];
			if (isVEB) {
				//eslint-disable-next-line
				Object.values(BBI_VEB_ENROLLMENT_SHORT_NAMES).forEach((renderedPlan) => {
					_VEBEnrollmentBarChartData.forEach(({ plan, count }) => {
						if (renderedPlan === plan) _storedVEBEnrollmentArrayData.push(count);
					});
				});
			}

			// Process employee count for pie chart
			const _totalEmployeePieChartData: any[] = activeEmployeeCountWithGender.genders;
			const _storedTotalEmployeeArrayData: number[] = [];

			// console.log("RESPONSE_FROM_DATA", _totalEmployeePieChartData);

			const sortedGenderData = _totalEmployeePieChartData.sort((a, b) => {
				return a.gender === "FEMALE" ? -1 : 1;
			});

			sortedGenderData.forEach(({ gender, count }) => {
				gender === "FEMALE"
					? _storedTotalEmployeeArrayData.unshift(count)
					: _storedTotalEmployeeArrayData.push(count);
			});

			// Set State Updates
			setHighlightsTotalCount({
				totalCoreEnrollments: _totalActiveCoreEnrollmentCount,
				totalEmployeeCount: _totalActiveEmployeeCount,
				totalVebEnrollments: _totalActiveVEBEnrollmentCount
			});

			setActiveEmployeeCount({
				male: _totalmaleEmployeeCount,
				female: _totalfemaleEmployeeCount
			});

			setCoreEnrollmentsGraphData({
				labels: ["HP", "DT", "VI"],
				datasets: [
					{
						data: _storedCoreEnrollmentArrayData,
						backgroundColor: [
							"#F08080",
							"#F28C87",
							"#F59D92",
							"#F6A296",
							"#F8AD9D",
							"#FAB9A4",
							"#FBC4AB",
							"#FDCFB2",
							"#FFDAB9"
						],
						borderColor: [
							"#F08080",
							"#F28C87",
							"#F59D92",
							"#F6A296",
							"#F8AD9D",
							"#FAB9A4",
							"#FBC4AB",
							"#FDCFB2",
							"#FFDAB9"
						],
						borderWidth: 1
					}
				]
			});

			if (isVEB) {
				setVEBEnrollmentsGraphData({
					labels: [
						"Cancer",
						"Whole Life",
						"STD",
						"Critical Illness",
						"HI",
						"Accident",
						"GLI",
						"FPP",
						"Doc & Rx",
						"Vision",
						"Dental"
					],
					datasets: [
						{
							data: _storedVEBEnrollmentArrayData,
							backgroundColor: [
								"#615ae7",
								"#716be1",
								"#7772df",
								"#8681df",
								"#9490e0",
								"#9995df",
								"#a39fdf",
								"#b0ade1",
								"#bebce1",
								"#cccbe7 ",
								"#d8d7ea"
							],
							borderColor: [
								"#615ae7",
								"#716be1",
								"#7772df",
								"#8681df",
								"#9490e0",
								"#9995df",
								"#a39fdf",
								"#b0ade1",
								"#bebce1",
								"#cccbe7 ",
								"#d8d7ea"
							],
							borderWidth: 1
						}
					]
				});
			}

			// console.log("Response I need", _storedTotalEmployeeArrayData);

			setTotalEmployeeGraphData({
				labels: ["Female", "Male"],
				datasets: [
					{
						data: _storedTotalEmployeeArrayData,
						backgroundColor: ["#4059ad", "#6b9ac4"],
						hoverBackgroundColor: ["#617acc", "#81b3df"]
					}
				]
			});
		}
	}, []);

	useEffect(() => {
		queryGroupNumber && getInitBenefitSummaryHighlightDetails(queryGroupNumber as number);

		if (refresh) {
			// console.log("Refresh + API CALL - componennt 01");
			queryGroupNumber && getInitBenefitSummaryHighlightDetails(queryGroupNumber as number);
			setRefresh(false);
		}
	}, [getInitBenefitSummaryHighlightDetails, queryGroupNumber, refresh, setRefresh]);
	// console.log("MAINCONSOLECHECKFORTHIS-------->>>>>>", coreEnrollmentsGraphData);
	return (
		<div className="enrollment-card-grid">
			<div className="enrollment-card-grid-container">
				<div className="enrollment-card-grid-item core-enrollments">
					{hasCorePlans === false ? (
						<NoEnrollmentFoundCard title="No Core Plans Found" />
					) : (
						<EnrollmentCard
							cardUIProps={{
								cardColor: "#E7E7E7",
								cardTitle: "Total Core Enrollments",
								overallTotal: highlightsTotalCount.totalCoreEnrollments
							}}
							chartProps={{
								data: coreEnrollmentsGraphData,
								options: coreEnrollmentOptions
							}}
							// chart={barChart}
							chartLegend={coreEnrollmentsLegendData}
						/>
					)}
				</div>
				<div className="enrollment-card-grid-item total-employee">
					<EnrollmentEmployeeCard
						cardUIProps={{
							cardColor: "#dae4fe",
							cardTitle: "Total Employee Count",
							overallTotal: highlightsTotalCount.totalEmployeeCount,
							maleCount: activeEmployeeCount.male,
							femaleCount: activeEmployeeCount.female
						}}
						chartProps={{
							data: totalEmployeeGraphData,
							options: totalEmployeeoptions
						}}
					/>
				</div>
				<div className="enrollment-card-grid-item veb-enrollments">
					{isVEB ? (
						<EnrollmentCard
							cardUIProps={{
								cardColor: "#E7E7E7",
								cardTitle: "Total VEB Enrollments",
								overallTotal: highlightsTotalCount.totalVebEnrollments,
								marginTopText: "0.7rem"
							}}
							chartProps={{
								data: VEBEnrollmentsGraphData,
								options: vebEnrollmentOptions
							}}
							// chart={barChart}
							chartLegend={VEBEnrollmentsLegendData}
						/>
					) : (
						<NoEnrollmentFoundCard />
					)}
				</div>
			</div>
		</div>
	);
};

export default EnrollmentCardGridPFB;
